import { z } from 'zod';

// Base schema for documents with timestamps
const baseDocumentSchema = z.object({
  tenantId: z.string().min(1),
  createdAt: z.any(), // Firestore Timestamp
  updatedAt: z.any(), // Firestore Timestamp
});

// Contact schema (shared)
export const contactSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  phone: z.string().optional(),
});

export const waiverSummaryFieldsSchema = z.object({
  waiverSignedAt: z.any().optional(),
  waiverSignatureId: z.string().optional(),
  waiverTemplateId: z.string().optional(),
});

// Tenant
export const tenantSchema = baseDocumentSchema.extend({
  name: z.string().min(1),
  slug: z.string().min(1).regex(/^[a-z0-9-]+$/),
  businessType: z.string().min(1).optional(),
  ownerUid: z.string().min(1).optional(),
  defaultTimezone: z.string().min(1).optional(),
  status: z.enum(['pending_payment', 'active', 'past_due', 'cancelled', 'suspended', 'inactive']),
  billingStatus: z.enum(['incomplete', 'trialing', 'active', 'past_due', 'unpaid', 'canceled']).nullable().optional(),
  planId: z.string().nullable().optional(),
  stripeCustomerId: z.string().nullable().optional(),
  stripeSubscriptionId: z.string().nullable().optional(),
  onboardingStatus: z.enum(['not_started', 'in_progress', 'complete']).nullable().optional(),
  businessProfile: z.object({
    website: z.string().url().or(z.literal('')).optional(),
    email: z.string().email().or(z.literal('')).optional(),
    phone: z.string().optional(),
    address: z.object({
      line1: z.string().optional(),
      line2: z.string().optional(),
      city: z.string().optional(),
      region: z.string().optional(),
      postalCode: z.string().optional(),
      country: z.string().optional(),
    }).optional(),
  }).optional(),
  businessLogic: z.object({
    workingPlan: z.record(z.any().nullable()).optional(),
    defaultBreaks: z.array(z.object({
      id: z.string().min(1),
      day: z.string().min(1),
      start: z.string().min(1),
      end: z.string().min(1),
    })).optional(),
    blockedPeriods: z.array(z.object({
      id: z.string().min(1),
      label: z.string().optional(),
      startTime: z.any(),
      endTime: z.any(),
    })).optional(),
    bookingTimeoutMinutes: z.number().int().min(0).optional(),
  }).optional(),
  branding: z.object({
    logoUrl: z.string().url().optional(),
    primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    secondaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  }).optional(),
  stripe: z.object({
    mode: z.enum(['perTenantKeys', 'connect']),
    publishableKeyRef: z.string().optional(),
    secretKeyRef: z.string().optional(),
    webhookSecretRef: z.string().optional(),
  }).optional(),
  bookingSettings: z.object({
    cancelCutoffHours: z.number().int().min(0).optional(),
    rescheduleCutoffHours: z.number().int().min(0).optional(),
    publicRateLimit: z.object({
      maxAttempts: z.number().int().min(1).optional(),
      windowMinutes: z.number().int().min(1).optional(),
    }).optional(),
    contactFields: z.record(z.object({
      display: z.boolean().optional(),
      required: z.boolean().optional(),
    })).optional(),
    customFields: z.array(z.object({
      id: z.string().min(1),
      label: z.string(),
      display: z.boolean().optional(),
      required: z.boolean().optional(),
    })).optional(),
  }).optional(),
});

export const tenantInputSchema = tenantSchema.omit({ createdAt: true, updatedAt: true });
export const tenantUpdateSchema = tenantSchema.omit({ 
  tenantId: true, 
  createdAt: true, 
  updatedAt: true 
}).partial();

// Location
export const locationSchema = baseDocumentSchema.extend({
  name: z.string().min(1),
  timezone: z.string().min(1),
  address: z.string().optional(),
});

export const locationInputSchema = locationSchema.omit({ createdAt: true, updatedAt: true });
export const locationUpdateSchema = locationSchema.omit({ 
  tenantId: true, 
  createdAt: true, 
  updatedAt: true 
}).partial();

// User
export const userRoleSchema = z.enum(['owner', 'manager', 'coach', 'staff']);

export const userSchema = baseDocumentSchema.extend({
  email: z.string().email(),
  role: userRoleSchema,
  locationIds: z.array(z.string()),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  photoUrl: z.string().url().optional(),
  serviceIds: z.array(z.string()).optional(),
});

export const userInputSchema = userSchema.omit({ createdAt: true, updatedAt: true });
export const userUpdateSchema = userSchema.omit({ 
  tenantId: true, 
  createdAt: true, 
  updatedAt: true 
}).partial();

// Lead
export const pipelineStageSchema = z.enum([
  'new',
  'contacted',
  'qualified',
  'proposal',
  'negotiation',
  'won',
  'lost',
]);

export const leadSchema = baseDocumentSchema.extend({
  contact: contactSchema,
  emailLower: z.string().email().optional(),
  source: z.string().optional(),
  pipelineStage: pipelineStageSchema,
  ownerId: z.string().optional(),
  tags: z.array(z.string()),
  nextActionAt: z.any().optional(), // Firestore Timestamp
  notes: z.string(),
}).merge(waiverSummaryFieldsSchema);

export const leadInputSchema = leadSchema.omit({ createdAt: true, updatedAt: true });
export const leadUpdateSchema = leadSchema.omit({ 
  tenantId: true, 
  createdAt: true, 
  updatedAt: true 
}).partial();

// Member
export const memberStatusSchema = z.enum(['active', 'paused', 'canceled']);

export const memberSchema = baseDocumentSchema.extend({
  authUid: z.string().nullable().optional(),
  emailLower: z.string().email().optional(),
  barcodeId: z.string().optional(),
  contact: contactSchema,
  status: memberStatusSchema,
  stripeCustomerId: z.string().optional(),
  planId: z.string().optional(),
  currentSubscriptionId: z.string().optional(),
  attendanceCount: z.number().int().min(0),
  joinedAt: z.any().optional(), // Firestore Timestamp
}).merge(waiverSummaryFieldsSchema);

export const memberInputSchema = memberSchema.omit({ createdAt: true, updatedAt: true });
export const memberUpdateSchema = memberSchema.omit({ 
  tenantId: true, 
  createdAt: true, 
  updatedAt: true 
}).partial();

// Plan
export const taxBehaviorSchema = z.enum(['inclusive', 'exclusive']);

export const planPriceSchema = z.object({
  id: z.string(),
  interval: z.enum(['month', 'year', 'week', 'day']),
  currency: z.string().length(3).toLowerCase(),
  amount: z.number().int().positive(),
});

export const planSchema = baseDocumentSchema.extend({
  name: z.string().min(1),
  description: z.string(),
  features: z.array(z.string()),
  price: z.number().int().nonnegative(),
  stripeProductId: z.string().optional(),
  priceIds: z.array(planPriceSchema).optional(),
  stripeSyncedAt: z.any().optional(), // Timestamp
  taxBehavior: taxBehaviorSchema.optional(),
  active: z.boolean(),
});

export const planInputSchema = planSchema.omit({ createdAt: true, updatedAt: true });
export const planUpdateSchema = planSchema.omit({ 
  tenantId: true, 
  createdAt: true, 
  updatedAt: true 
}).partial();

// Subscription
export const subscriptionStatusSchema = z.enum([
  'active',
  'past_due',
  'unpaid',
  'canceled',
  'incomplete',
  'incomplete_expired',
  'trialing',
  'paused',
]);

export const subscriptionSchema = baseDocumentSchema.extend({
  memberId: z.string().min(1),
  stripeSubscriptionId: z.string().min(1),
  status: subscriptionStatusSchema,
  currentPeriodEnd: z.any(), // Firestore Timestamp
  currentPeriodStart: z.any().optional(), // Firestore Timestamp
  cancelAt: z.any().optional(), // Firestore Timestamp
  canceledAt: z.any().optional(), // Firestore Timestamp
  pauseCollection: z.object({
    behavior: z.enum(['keep_as_draft', 'mark_uncollectible', 'void']),
    resumesAt: z.any().optional(), // Firestore Timestamp
  }).optional(),
});

export const subscriptionInputSchema = subscriptionSchema.omit({ createdAt: true, updatedAt: true });
export const subscriptionUpdateSchema = subscriptionSchema.omit({ 
  tenantId: true, 
  createdAt: true, 
  updatedAt: true 
}).partial();

// Invoice
export const invoiceStatusSchema = z.enum([
  'draft',
  'open',
  'paid',
  'uncollectible',
  'void',
]);

export const invoiceSchema = baseDocumentSchema.extend({
  memberId: z.string().min(1),
  stripeInvoiceId: z.string().min(1),
  status: invoiceStatusSchema,
  hostedInvoiceUrl: z.string().url().optional(),
  invoicePdf: z.string().url().optional(),
  total: z.number().int(),
  currency: z.string().length(3).toLowerCase(),
  dueDate: z.any().optional(), // Firestore Timestamp
  paidAt: z.any().optional(), // Firestore Timestamp
});

export const invoiceInputSchema = invoiceSchema.omit({ createdAt: true, updatedAt: true });
export const invoiceUpdateSchema = invoiceSchema.omit({ 
  tenantId: true, 
  createdAt: true, 
  updatedAt: true 
}).partial();

// Appointment
export const appointmentStatusSchema = z.enum(['confirmed', 'pending', 'completed', 'canceled', 'no_show']);
export const customerTypeSchema = z.enum(['member', 'guest']);
export const paymentStatusSchema = z.enum(['pending', 'paid', 'refunded', 'not_required']);
export const appointmentSourceSchema = z.enum(['public_portal', 'admin_booking', 'member_portal']);

export const guestInfoSchema = z.object({
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  email: z.string().email().optional(),
  phone: z.string().min(1).optional(),
  address: z.string().min(1).optional(),
  city: z.string().min(1).optional(),
  postalCode: z.string().min(1).optional(),
});

export const appointmentSchema = baseDocumentSchema.extend({
  startTime: z.any(), // Firestore Timestamp
  endTime: z.any(), // Firestore Timestamp
  serviceId: z.string().min(1),
  providerId: z.string().min(1),
  customerType: customerTypeSchema,
  customerId: z.string().optional(), // memberId if member
  guestInfo: guestInfoSchema.optional(),
  locationId: z.string().optional(),
  status: appointmentStatusSchema,
  paymentStatus: paymentStatusSchema.optional(),
  stripePaymentIntentId: z.string().optional(),
  stripeCheckoutSessionId: z.string().optional(),
  paymentExpiresAt: z.any().optional(),
  emailReminderSent: z.boolean(),
  reminderSentAt: z.any().optional(), // Firestore Timestamp
  notes: z.string().optional(),
  customerNotes: z.string().optional(),
  customFields: z.array(z.object({
    id: z.string().min(1),
    label: z.string(),
    value: z.string(),
  })).optional(),
  source: appointmentSourceSchema,
  publicAccessHash: z.string().optional(),
  googleCalendarEventId: z.string().optional(),
});

export const appointmentInputSchema = appointmentSchema.omit({ createdAt: true, updatedAt: true });
export const appointmentUpdateSchema = appointmentSchema.omit({
  tenantId: true,
  createdAt: true,
  updatedAt: true,
}).partial();

export const waiverTemplateSchema = baseDocumentSchema.extend({
  version: z.number().int().min(1),
  title: z.string().min(1),
  originalFileName: z.string().min(1),
  storagePath: z.string().min(1),
  contentType: z.string().min(1),
  fileSize: z.number().int().positive(),
  status: z.enum(['active', 'archived']),
  uploadedBy: z.string().min(1),
});

export const waiverSignatureSchema = baseDocumentSchema.extend({
  templateId: z.string().min(1),
  templateVersion: z.number().int().min(1),
  emailLower: z.string().email(),
  signerFirstName: z.string().min(1),
  signerLastName: z.string().min(1),
  signerEmail: z.string().email(),
  signerPhone: z.string().nullable().optional(),
  typedSignature: z.string().min(1),
  accepted: z.literal(true),
  memberId: z.string().nullable().optional(),
  leadId: z.string().nullable().optional(),
  signedAt: z.any(),
  ipAddress: z.string().nullable().optional(),
  userAgent: z.string().nullable().optional(),
});

// Time utilities for schedules
export const timeRangeSchema = z.object({
  start: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/), // "HH:MM" 24h format
  end: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/),
});

export const dayScheduleSchema = z.object({
  isWorking: z.boolean(),
  start: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/),
  end: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/),
  breaks: z.array(timeRangeSchema),
});

// ProviderSchedule
export const workingPlanSchema = z.object({
  monday: dayScheduleSchema.nullable(),
  tuesday: dayScheduleSchema.nullable(),
  wednesday: dayScheduleSchema.nullable(),
  thursday: dayScheduleSchema.nullable(),
  friday: dayScheduleSchema.nullable(),
  saturday: dayScheduleSchema.nullable(),
  sunday: dayScheduleSchema.nullable(),
});

export const providerScheduleSchema = baseDocumentSchema.extend({
  providerId: z.string().min(1),
  workingPlan: workingPlanSchema,
  exceptions: z.record(dayScheduleSchema.nullable()),
  timezone: z.string().min(1),
});

export const providerScheduleInputSchema = providerScheduleSchema.omit({ createdAt: true, updatedAt: true });
export const providerScheduleUpdateSchema = providerScheduleSchema.omit({
  tenantId: true,
  createdAt: true,
  updatedAt: true,
}).partial();

// Service
export const serviceVisibilitySchema = z.enum(['public', 'private']);
export const availabilitiesTypeSchema = z.enum(['flexible', 'fixed']);
export const paymentPolicySchema = z.enum(['not_required', 'pay_later', 'prepay']);
export const serviceBookingModeSchema = z.enum(['provider_appointment', 'class_session']);
export const weekdayKeySchema = z.enum(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']);
export const recurringClassTimeSchema = z.object({
  id: z.string().min(1),
  days: z.array(weekdayKeySchema).min(1),
  startTime: z.string().regex(/^\d{2}:\d{2}$/),
  timezone: z.string().optional(),
  capacity: z.number().int().min(1).optional(),
  locationId: z.string().optional(),
  instructorId: z.string().optional(),
  isActive: z.boolean(),
});

export const serviceSchema = baseDocumentSchema.extend({
  name: z.string().min(1),
  description: z.string().optional(),
  duration: z.number().int().positive(), // minutes
  price: z.number().int().nonnegative().optional(), // cents
  currency: z.string().length(3).toLowerCase().optional(),
  paymentPolicy: paymentPolicySchema.optional(),
  bookingMode: serviceBookingModeSchema.optional(),
  visibility: serviceVisibilitySchema,
  locationId: z.string().optional(),
  availabilitiesType: availabilitiesTypeSchema,
  attendantsNumber: z.number().int().min(1), // 1 = individual, >1 = group class
  providerIds: z.array(z.string()),
  recurringClassTimes: z.array(recurringClassTimeSchema).optional(),
  includedPlanIds: z.array(z.string()).optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(), // hex color
  imageUrl: z.string().url().optional(),
  displayOrder: z.number().int(),
  isActive: z.boolean(),
  categoryId: z.string().optional(),
});

export const serviceInputSchema = serviceSchema.omit({ createdAt: true, updatedAt: true });
export const serviceUpdateSchema = serviceSchema.omit({
  tenantId: true,
  createdAt: true,
  updatedAt: true,
}).partial();

// ServiceCategory
export const serviceCategorySchema = baseDocumentSchema.extend({
  name: z.string().min(1),
  description: z.string().optional(),
  displayOrder: z.number().int(),
  isActive: z.boolean(),
});

export const serviceCategoryInputSchema = serviceCategorySchema.omit({ createdAt: true, updatedAt: true });
export const serviceCategoryUpdateSchema = serviceCategorySchema.omit({
  tenantId: true,
  createdAt: true,
  updatedAt: true,
}).partial();

// Unavailability
export const unavailabilitySchema = baseDocumentSchema.extend({
  providerId: z.string().min(1),
  startTime: z.any(), // Firestore Timestamp
  endTime: z.any(), // Firestore Timestamp
  reason: z.string().optional(),
  createdBy: z.string().min(1),
});

export const unavailabilityInputSchema = unavailabilitySchema.omit({ createdAt: true, updatedAt: true });
export const unavailabilityUpdateSchema = unavailabilitySchema.omit({
  tenantId: true,
  createdAt: true,
  updatedAt: true,
}).partial();

// Check-in
export const checkInMethodSchema = z.enum(['qr', 'manual']);

export const checkInSchema = baseDocumentSchema.extend({
  memberId: z.string().min(1),
  locationId: z.string().min(1),
  timestamp: z.any(), // Firestore Timestamp
  method: checkInMethodSchema,
  verifiedBy: z.string().optional(),
});

export const checkInInputSchema = checkInSchema.omit({ createdAt: true, updatedAt: true });

// Activity (Audit Log)
export const activitySubjectRefSchema = z.object({
  collection: z.string().min(1),
  id: z.string().min(1),
});

export const activitySchema = baseDocumentSchema.extend({
  actorUserId: z.string().min(1),
  verb: z.string().min(1),
  subjectRef: activitySubjectRefSchema,
  diff: z.record(z.unknown()).optional(),
  metadata: z.record(z.unknown()).optional(),
});

export const activityInputSchema = activitySchema.omit({ createdAt: true, updatedAt: true });
